/** Employer plan comparison shown on organisation billing. */
import { CURRENT_CLIENT } from "@/constants"

export type EmployerPlanTierId = "free" | "pro" | "enterprise"

export type TierCell =
  | { kind: "text"; label: string }
  | { kind: "included"; label?: string }
  | { kind: "excluded"; label?: string }

export type EmployerPlanTier = {
  id: EmployerPlanTierId
  name: string
  price: string
  cadence: string
}

export type EmployerPlanFeatureRow = {
  feature: string
  cells: Record<EmployerPlanTierId, TierCell>
}

/** Opens the mail app with a subject. No inbox is set until one is confirmed. */
const BRAND = CURRENT_CLIENT.name || "Yes Human"

export const ENTERPRISE_CONTACT_HREF = `mailto:?subject=${encodeURIComponent(`${BRAND} Enterprise plan`)}&body=${encodeURIComponent("I would like to talk about the Enterprise plan.")}`

export const EMPLOYER_PLAN_TIERS: EmployerPlanTier[] = [
  { id: "free", name: "Free", price: "AUD 0", cadence: "per month" },
  { id: "pro", name: "Pro", price: "AUD 199", cadence: "per month" },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "AUD 10k–30k",
    cadence: "per year, to start",
  },
]

export const EMPLOYER_PLAN_FEATURE_ROWS: EmployerPlanFeatureRow[] = [
  {
    feature: "Job ads",
    cells: {
      free: { kind: "text", label: "1–2 / month" },
      pro: { kind: "text", label: "5 / month" },
      enterprise: { kind: "text", label: "Negotiated or unlimited" },
    },
  },
  {
    feature: "Candidate search",
    cells: {
      free: { kind: "text", label: "Basic" },
      pro: { kind: "text", label: "Advanced" },
      enterprise: { kind: "text", label: "Advanced" },
    },
  },
  {
    feature: "Candidate matching",
    cells: {
      free: { kind: "text", label: "Top 5–10 matches" },
      pro: { kind: "text", label: "Rank full pool" },
      enterprise: { kind: "text", label: "Rank full pool" },
    },
  },
  {
    feature: "Match explanation",
    cells: {
      free: { kind: "text", label: "Score and top 2 lines" },
      pro: { kind: "text", label: "Full matching intelligence" },
      enterprise: { kind: "text", label: "Full matching intelligence" },
    },
  },
  {
    feature: "Change ranking or skills",
    cells: {
      free: { kind: "excluded" },
      pro: { kind: "included" },
      enterprise: { kind: "included" },
    },
  },
  {
    feature: "Invites to apply",
    cells: {
      free: { kind: "text", label: "Limited" },
      pro: { kind: "text", label: "Monthly allowance" },
      enterprise: { kind: "text", label: "Full" },
    },
  },
  {
    feature: "Applicant tracking",
    cells: {
      free: { kind: "excluded" },
      pro: { kind: "included" },
      enterprise: { kind: "included" },
    },
  },
  {
    feature: "Seats",
    cells: {
      free: { kind: "text", label: "1" },
      pro: { kind: "text", label: "2" },
      enterprise: { kind: "text", label: "Multi-user" },
    },
  },
  {
    feature: `Success fee for ${BRAND}-sourced candidates`,
    cells: {
      free: { kind: "text", label: "0%" },
      pro: { kind: "text", label: "3%" },
      enterprise: { kind: "text", label: "3%" },
    },
  },
  {
    feature: "Managed services",
    cells: {
      free: { kind: "text", label: "Paid option, 5–8%" },
      pro: { kind: "text", label: "Paid option, 5–8%" },
      enterprise: { kind: "text", label: "Paid option, 5–8%" },
    },
  },
  {
    feature: "Support",
    cells: {
      free: { kind: "excluded" },
      pro: { kind: "text", label: "Alias" },
      enterprise: { kind: "text", label: "Account manager" },
    },
  },
]

/** Live subscription id → comparison column. */
export function employerTierForPlanId(
  planId: string | null | undefined,
): EmployerPlanTierId | null {
  if (planId === "free") return "free"
  if (planId === "paid_1") return "pro"
  if (planId === "enterprise") return "enterprise"
  return null
}

/** Column to emphasise: the plan you can move to, or the one you are on. */
export function emphasizedEmployerTier(
  current: EmployerPlanTierId | null,
): EmployerPlanTierId {
  if (current === "pro" || current === "enterprise") return current
  return "pro"
}
